import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import StandardNavbar from '../components/StandardNavbar';
import Footer from '../components/Footer';
import { ArrowLeft, ChevronDown, ChevronUp, Mail } from 'lucide-react';

function PaginaCMS() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [pagina, setPagina] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [seccionesAbiertas, setSeccionesAbiertas] = useState({});

  useEffect(() => {
    const fetchPagina = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('=== INICIANDO CARGA DE PÁGINA CMS ===');
        console.log('🔍 Slug:', slug);
        console.log('🌐 API URL:', process.env.REACT_APP_API_URL);
        
        const apiUrl = `${process.env.REACT_APP_API_URL}/paginas-cms/${slug}`;
        console.log('📡 Llamando a:', apiUrl);
        
        const response = await fetch(apiUrl, {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        
        console.log('📥 Response status:', response.status);
        console.log('📥 Response headers:', Object.fromEntries(response.headers.entries()));
        
        if (response.ok) {
          const data = await response.json();
          console.log('✅ Página obtenida completa:', data);
          console.log('   - Título:', data.titulo);
          console.log('   - Descripción:', data.descripcion);
          console.log('   - Secciones:', data.secciones?.length || 0);
          
          if (data.secciones && data.secciones.length > 0) {
            console.log('📋 Primera sección:', {
              titulo: data.secciones[0].titulo,
              contenidoLength: data.secciones[0].contenido?.length || 0
            });
          }
          
          setPagina(data);
          
          // Abrir la primera sección por defecto si existen secciones
          //if (data.secciones && Array.isArray(data.secciones) && data.secciones.length > 0) {
          //  setSeccionesAbiertas({ [data.secciones[0].id]: true });
          //}
        } else {
          const errorText = await response.text();
          console.error('❌ Error response:', response.status, errorText);
          setError('Página no encontrada');
        }
      } catch (err) {
        console.error('❌ Error cargando página:', err);
        console.error('   Stack:', err.stack);
        setError('Error al cargar la página');
      } finally {
        setLoading(false);
        console.log('=== FIN CARGA PÁGINA CMS ===\n');
      }
    };

    if (slug) {
      fetchPagina();
    } else {
      console.warn('⚠️ No hay slug definido');
    }
  }, [slug]);

  const toggleSeccion = (seccionId) => {
    setSeccionesAbiertas(prev => ({
      ...prev,
      [seccionId]: !prev[seccionId]
    }));
  };

  if (loading) {
    return (
      <>
        <StandardNavbar />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2AA8B0] mx-auto mb-4"></div>
            <p className="text-gray-600">Cargando información...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error || !pagina) {
    return (
      <>
        <StandardNavbar />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Página no encontrada</h2>
            <p className="text-gray-600 mb-6">{error || 'La página que buscas no existe'}</p>
            <button
              onClick={() => navigate('/')}
              className="bg-[#2AA8B0] text-white px-6 py-3 rounded-lg hover:bg-[#1E3A5F] transition-colors"
            >
              Volver al inicio
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Si no hay secciones, mostrar contenido simple
  const haySecciones = pagina.secciones && Array.isArray(pagina.secciones) && pagina.secciones.length > 0;

  return (
    <>
      <Helmet>
        <title>{pagina.titulo} | Noche y Día Turismo</title>
        <meta name="description" content={pagina.descripcion || pagina.titulo} />
      </Helmet>
      
      <StandardNavbar />
      
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-[#2AA8B0] to-[#1E3A5F] text-white py-12">
          <div className="container mx-auto px-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
            >
              <ArrowLeft size={20} />
              Volver
            </button>
            <h1 className="text-4xl font-bold mb-3">{pagina.titulo}</h1>
            {pagina.descripcion && (
              <p className="text-blue-100 text-lg">{pagina.descripcion}</p>
            )}
            {pagina.fechaActualizacion && (
              <p className="text-blue-200 text-sm mt-4">
                Última actualización: {new Date(pagina.fechaActualizacion).toLocaleDateString('es-ES', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            )}
          </div>
        </div>

        {/* Contenido */}
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto">
            {haySecciones ? (
              /* Secciones Desplegables */
              <div className="space-y-4">
                {pagina.secciones.map((seccion, index) => (
                  <div 
                    key={seccion.id} 
                    className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200"
                  >
                    <button
                      onClick={() => toggleSeccion(seccion.id)}
                      className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <span className="flex items-center justify-center w-8 h-8 rounded-full bg-[#2AA8B0] text-white font-semibold text-sm">
                          {index + 1}
                        </span>
                        <h2 className="text-lg font-semibold text-gray-900">
                          {seccion.titulo}
                        </h2>
                      </div>
                      {seccionesAbiertas[seccion.id] ? (
                        <ChevronUp size={24} className="text-gray-400" />
                      ) : (
                        <ChevronDown size={24} className="text-gray-400" />
                      )}
                    </button>
                    
                    {seccionesAbiertas[seccion.id] && (
                      <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                        <div 
                          className="prose prose-gray max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-a:text-[#2AA8B0] prose-strong:text-gray-900"
                          dangerouslySetInnerHTML={{ __html: seccion.contenido }}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              /* Contenido simple sin secciones */
              <div className="bg-white rounded-lg shadow-md p-8">
                <div 
                  className="prose prose-gray max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-a:text-[#2AA8B0] prose-strong:text-gray-900"
                  dangerouslySetInnerHTML={{ __html: pagina.contenido || '<p>No hay contenido disponible.</p>' }}
                />
              </div>
            )}

            {/* Call to Action */}
            <div className="mt-12 bg-gradient-to-r from-[#2AA8B0] to-[#1E3A5F] rounded-lg p-8 text-center text-white">
              <Mail size={48} className="mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-2">¿Tienes alguna pregunta?</h3>
              <p className="text-blue-100 mb-6">
                Nuestro equipo está disponible para ayudarte con cualquier consulta
              </p>
              <button
                onClick={() => navigate('/contacto')}
                className="bg-white text-[#2AA8B0] px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                Contáctanos
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </>
  );
}

export default PaginaCMS;